import { Head } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/retention" },
  { title: "Housekeeping", href: "/admin/cron/housekeeping" },
]

interface HousekeepingResult {
  reads_purged: number
  notifications_purged: number
  sensitive_subjects_purged: number
  restricted_logs_purged: number
  restricted_subjects_purged: number
  deleted_contacts_purged: number
  deleted_documents_purged: number
  deleted_evidences_purged: number
  deleted_expenses_purged: number
  deleted_gps_purged: number
  deleted_reports_purged: number
  deleted_schedules_purged: number
  deleted_subjects_purged: number
  deleted_time_entries_purged: number
  deleted_updates_purged: number
  log_entries: string[]
}

interface Props {
  result: HousekeepingResult
}

export default function Housekeeping({ result }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Hourly Housekeeping" />

      <div className="p-6">
        <h1 className="border-hpi-light-gray mb-6 border-b pb-2 text-2xl font-semibold">
          Hourly Housekeeping Results
        </h1>

        <Section title="Read Receipts &amp; Notifications (21-day cleanup)">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard label="Reads Purged" value={result.reads_purged} />
            <StatCard
              label="Notifications Purged"
              value={result.notifications_purged}
            />
          </div>
        </Section>

        <Section title="Sensitive Data (90-day retention)">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard
              label="Subjects Purged"
              value={result.sensitive_subjects_purged}
            />
          </div>
        </Section>

        <Section title="Restricted Data (1-year retention)">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard
              label="Restricted Logs Purged"
              value={result.restricted_logs_purged}
            />
            <StatCard
              label="Subjects Purged"
              value={result.restricted_subjects_purged}
            />
          </div>
        </Section>

        <Section title="Deleted Entries (1-year retention)">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard label="Contacts" value={result.deleted_contacts_purged} />
            <StatCard
              label="Documents"
              value={result.deleted_documents_purged}
            />
            <StatCard
              label="Evidence"
              value={result.deleted_evidences_purged}
            />
            <StatCard label="Expenses" value={result.deleted_expenses_purged} />
            <StatCard label="GPS" value={result.deleted_gps_purged} />
            <StatCard label="Reports" value={result.deleted_reports_purged} />
            <StatCard
              label="Schedules"
              value={result.deleted_schedules_purged}
            />
            <StatCard label="Subjects" value={result.deleted_subjects_purged} />
            <StatCard
              label="Time Entries"
              value={result.deleted_time_entries_purged}
            />
            <StatCard label="Updates" value={result.deleted_updates_purged} />
          </div>
        </Section>

        {result.log_entries.length > 0 ? (
          <div>
            <h2 className="mb-2 text-lg font-semibold">Purge Log</h2>
            <pre className="max-h-96 overflow-y-auto rounded bg-gray-50 p-4 text-sm text-gray-700">
              {result.log_entries.join("\n")}
            </pre>
          </div>
        ) : (
          <p className="text-gray-500">
            No records were eligible for purging in this run.
          </p>
        )}

        <p className="mt-4 text-sm text-gray-500">
          An email log has been sent to administrators.
        </p>
      </div>
    </AppLayout>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="mb-6">
      <h2 className="mb-3 text-lg font-semibold">{title}</h2>
      {children}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold">{value}</dd>
    </div>
  )
}
